import React from 'react';
import sharedStyles from './Home.module.scss';
import styles from './ReviewSetupBasicWizard.module.scss';
import { Stack, Inline } from 'components/layout';
import {
  AccordionContent,
  AccordionControls,
  AccordionGroup,
  AccordionHeader,
  Button,
  Icon,
  IconList,
  Link,
  Loading,
  ProgressBar,
  Text,
} from 'components/core';
import { useReviewSetupCompletion } from 'hooks/reviews/useReviewSetupCompletion';
import { useReviewSetupLinks } from 'hooks/reviews/useReviewSetupLinks';

const ReviewSetupBasicWizardWrapper = ({ isOpen }: { isOpen: boolean }) => {
  return <ReviewSetupBasicWizard isOpen={isOpen} />;
};

const ReviewSetupBasicWizard = ({ isOpen }: { isOpen: boolean }) => {
  const {
    reviewSetupLink,
    eligibilityCriteriaLink,
    highlightsLink,
    exclusionReasonsLink,
  } = useReviewSetupLinks();

  const {
    isLoading,
    isComplete,
    completedCount,
    eligibilityComplete,
    highlightsComplete,
    exclusionReasonsComplete,
  } = useReviewSetupCompletion();

  return (
    <AccordionGroup
      className={sharedStyles.AccordionGroup}
      initiallyOpen={isOpen}
    >
      <AccordionHeader
        className={`${sharedStyles.AccordionHeader} ${sharedStyles.ReviewSetupAccordionHeader}`}
        chevronPosition="right"
        level={3}
      >
        <span className={sharedStyles.AccordionHeaderTitle}>Review setup</span>
        {!isLoading && (
          <AccordionControls>
            <Inline
              spacing={4}
              className={sharedStyles.ControlsLinkContainer}
              alignX="right"
            >
              <div className={sharedStyles.ReviewSetupProgressBar}>
                <ProgressBar
                  type="success"
                  label="Review setup progress"
                  value={isLoading ? 0 : completedCount}
                  min={0}
                  max={3}
                  showCaption={!isLoading}
                />
              </div>

              <Button
                size="sm"
                type={isComplete ? 'neutral' : 'brand'}
                variant={isComplete ? 'outline' : 'solid'}
                to={reviewSetupLink}
                className={sharedStyles.PrimaryActionButton}
              >
                {isComplete ? 'Edit' : 'Setup'}
              </Button>
            </Inline>
          </AccordionControls>
        )}
      </AccordionHeader>
      <AccordionContent>
        <Stack spacing={3} className={styles.progressPanel}>
          <Text size="md">
            {isComplete
              ? 'Keep these updated as your review evolves to support a consistent, transparent and reproducible process.'
              : 'Setting these up now helps your team screen and extract data more consistently and accurately.'}
          </Text>
          {isLoading ? (
            <Loading />
          ) : (
            <>
              <ProgressBarLegend
                title="Eligibility criteria"
                link={eligibilityCriteriaLink}
                isComplete={eligibilityComplete}
                size="md"
              />
              <ProgressBarLegend
                title="Highlights"
                link={highlightsLink}
                isComplete={highlightsComplete}
                size="md"
              />
              <ProgressBarLegend
                title="Full text exclusion reasons"
                link={exclusionReasonsLink}
                isComplete={exclusionReasonsComplete}
                size="md"
              />
            </>
          )}
        </Stack>
      </AccordionContent>
    </AccordionGroup>
  );
};

// TODO: Move this properly into a core component
export const ProgressBarLegend = ({
  title,
  link,
  isComplete = false,
  size = 'sm',
}: {
  title: string;
  link: string | URL;
  isComplete: boolean;
  size?: 'sm' | 'md' | 'lg';
}) => {
  return (
    <Inline component="li" alignX="left" spacing={3} key={title}>
      <Icon
        size={size}
        icon={
          isComplete ? IconList.solid.faCircleCheck : IconList.light.faCircle
        }
        className={isComplete ? styles.done : styles.notStarted}
      />
      <Link to={link} variant="primary">
        <Text size={size}>{title}</Text>
      </Link>
    </Inline>
  );
};

export default ReviewSetupBasicWizardWrapper;
